/**
 * Chef Reminders Debugger
 * 
 * A debug component to test and observe Chef Reminders functionality.
 * Shows reminder status, statistics, and allows manual testing.
 */

import React, { useState, useEffect } from 'react';
import chefReminders from '../services/chefReminders';
import sessionReport from '../services/sessionReport';

const ChefRemindersDebugger = ({ showInUI = false }) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const [stats, setStats] = useState(null);
    const [reportData, setReportData] = useState(null);

    useEffect(() => {
        // Update stats periodically
        const interval = setInterval(() => {
            setStats(chefReminders.getReminderStats());
            setReportData(sessionReport.getReportData());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const toggleReminders = () => {
        const newEnabled = !isEnabled;
        setIsEnabled(newEnabled);
        chefReminders.setEnabled(newEnabled);
    };

    const testPhotoReminder = () => {
        chefReminders.triggerPhotoReminder('manual_test', 'Test photo reminder - want to snap a photo?');
    };

    const testCheckInReminder = () => {
        chefReminders.triggerCheckInReminder('manual_test', 'Test check-in - how is the cooking going?');
    };

    const clearReport = () => {
        sessionReport.clearReport();
        setReportData(null);
    };

    if (!showInUI) {
        return null; // Don't show any UI when showInUI is false
    }

    return (
        <div style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            width: '350px',
            maxHeight: '500px',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '15px',
            borderRadius: '8px',
            fontSize: '12px',
            zIndex: 9999,
            overflow: 'auto'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4 style={{ margin: 0 }}>Chef Reminders Debug</h4>
                <button
                    onClick={toggleReminders}
                    style={{
                        padding: '4px 8px',
                        backgroundColor: isEnabled ? '#4CAF50' : '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '10px'
                    }}
                >
                    {isEnabled ? 'ON' : 'OFF'}
                </button>
            </div>
            
            {/* Status */}
            <div style={{ marginBottom: '15px' }}>
                <h5 style={{ margin: '0 0 8px 0' }}>Status</h5>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>
                    <div>Enabled: {isEnabled ? '✅' : '❌'}</div>
                    <div>Session: {reportData?.sessionId || 'None'}</div>
                </div>
            </div>

            {/* Statistics */}
            {stats && (
                <div style={{ marginBottom: '15px' }}>
                    <h5 style={{ margin: '0 0 8px 0' }}>Reminder Stats</h5>
                    <div style={{ fontSize: '11px', opacity: 0.8 }}>
                        <div>Total Reminders: {stats.totalReminders}</div>
                        <div>Photos: {stats.reminderTypes.photo}</div>
                        <div>Check-ins: {stats.reminderTypes.checkin}</div>
                    </div>
                </div>
            )}

            {/* Session Report */}
            {reportData && (
                <div style={{ marginBottom: '15px' }}>
                    <h5 style={{ margin: '0 0 8px 0' }}>Session Report</h5>
                    <div style={{ fontSize: '11px', opacity: 0.8 }}>
                        <div>Photos: {reportData.summary.totalPhotos}</div>
                        <div>Check-ins: {reportData.summary.totalCheckIns}</div>
                        <div>Reminders: {reportData.summary.totalReminders}</div>
                    </div>
                </div>
            )}

            {/* Test Controls */}
            <div style={{ marginBottom: '15px' }}>
                <h5 style={{ margin: '0 0 8px 0' }}>Test Controls</h5>
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    <button
                        onClick={testPhotoReminder}
                        style={{
                            padding: '4px 8px',
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '10px'
                        }}
                    >
                        📸 Test Photo
                    </button>
                    <button
                        onClick={testCheckInReminder}
                        style={{
                            padding: '4px 8px',
                            backgroundColor: '#FF9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '10px'
                        }}
                    >
                        💬 Test Check-in
                    </button>
                    <button
                        onClick={clearReport}
                        style={{
                            padding: '4px 8px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '10px'
                        }}
                    >
                        🗑️ Clear
                    </button>
                </div>
            </div>

            {/* Recent Activity */}
            {reportData && (reportData.photos.length > 0 || reportData.checkIns.length > 0) && (
                <div>
                    <h5 style={{ margin: '0 0 8px 0' }}>Recent Activity</h5>
                    <div style={{ fontSize: '10px', opacity: 0.7, maxHeight: '100px', overflow: 'auto' }}>
                        {reportData.photos.slice(-3).map((photo, index) => (
                            <div key={photo.id} style={{ marginBottom: '4px' }}>
                                📸 Photo: {photo.trigger} (Step {photo.stepIndex + 1})
                            </div>
                        ))}
                        {reportData.checkIns.slice(-3).map((checkIn, index) => (
                            <div key={checkIn.id} style={{ marginBottom: '4px' }}>
                                💬 Check-in: {checkIn.trigger} (Step {checkIn.stepIndex + 1})
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChefRemindersDebugger;
